import { ServerError, useQuery } from '@sixfold/app-data-framework';
import { useErrorHandler } from '@sixfold/app-utils';
import { Localized } from '@sixfold/localization-component';
import { CustomError, isNil } from '@sixfold/typed-primitives';
import { useSetAtom } from 'jotai';
import * as React from 'react';

import { ViewerCompanyQuery, ViewerCompanyQueryVariables } from '../../lib/graphql';
import { useProductName } from '../../theme_wrapper';
import { companyDefaultDwellTimeThresholdState, userInCompanyConfigurationState } from '../atoms';
import { Company, CompanyPermissions, UserInCompany, UserOnTransporeon } from '../entities';
import { viewerCompany } from '../graphql';
import { getCompanyPermissions } from '../lib/entities';

export interface TourCounts {
  road: number;
  ocean: number;
  air: number;
}

export type ViewerCompany = {
  company: Company;
  permissions: CompanyPermissions;
  tourCounts: TourCounts;
  userInCompany: UserInCompany | null;
  userOnTransporeon: UserOnTransporeon | null;
};

export function useViewerCompanyQuery(companyId: string): {
  loading: boolean;
  error?: { type: 'not-found' | 'unknown'; message?: React.ReactNode };
  data?: ViewerCompany;
} {
  const PRODUCT_NAME = useProductName();
  const setCompanyDefaultDwellTimeThreshold = useSetAtom(companyDefaultDwellTimeThresholdState);
  const setUserInCompanyConfiguration = useSetAtom(userInCompanyConfigurationState);
  const result = useQuery<ViewerCompanyQuery, ViewerCompanyQueryVariables>(viewerCompany, {
    variables: { companyId },
  });

  React.useEffect(() => {
    if (result.data === undefined) {
      return;
    }
    setCompanyDefaultDwellTimeThreshold(
      result.data.viewer?.company?.defaultVehicleDwellTimeThresholdMinutes ?? undefined,
    );
    setUserInCompanyConfiguration(result.data.viewer?.company?.me?.configuration);
  }, [result.data, setCompanyDefaultDwellTimeThreshold, setUserInCompanyConfiguration]);

  const errorHandler = useErrorHandler();

  const viewer = result.data?.viewer;
  const company = viewer?.company;

  const permissions = React.useMemo(() => {
    if (isNil(company)) {
      return undefined;
    }

    return getCompanyPermissions({
      is_carrier: company.is_carrier,
      is_shipper: company.is_shipper,
      is_test: company.is_test,
      arePlacesEnabled: company.arePlacesEnabled,
      isVisibilityControlCenterDisabled: company.isVisibilityControlCenterDisabled,
      isInvitationFlowDisabled: company.isInvitationFlowDisabled,
      isMapVehicleLocationsEnabled: company.isMapVehicleLocationsEnabled,
      isGlobalAllocationEnabled: company.settings.allowAllocatingFromGlobalPool,
      isAutoAllocationEnabled: company.settings.enableAutoAllocation,
      isAutoAllocationFullVisibilityEnabled: company.settings.enableAutoAllocationFullVisibility,
      isOceanMapViewEnabled: company.isOceanMapViewEnabled,
      isEmailLoginEnabled: company.isEmailLoginEnabled,
      isAnalyticsEnabled: company.isVisibilityReportingVisible,
      isRoadUiDisabled: company.isRoadUiDisabled,
      isFilesUploadEnabled: company.isFilesUploadEnabled,
      isStandalonePagesEnabled: company.isStandalonePagesEnabled,
      isOceanUIEnabled: company.isOceanUIEnabled,
      isAirUIEnabled: company.isAirUIEnabled,
      isInlandVisibilityEnabled: company.isInlandVisibilityEnabled,
      isOceanAnalyticsEnabled: company.isOceanAnalyticsEnabled,
      isTableExportConfigEnabled: company.isTableExportConfigEnabled,
      isMaritimeIntelligenceEnabled: company.isMaritimeIntelligenceEnabled,
      isOceanTransportExcelUploadEnabled: company.isOceanTransportExcelUploadEnabled,
    });
  }, [company]);

  const mappedCompany: Company | undefined = React.useMemo(() => {
    if (isNil(company)) {
      return undefined;
    }
    const isShipper = company.is_shipper === true;
    const isCarrier = company.is_carrier === true;
    const isTest = company.is_test === true;

    return {
      ...company,
      is_carrier: isCarrier,
      is_shipper: isShipper,
      is_test: isTest,
      featureFlags: {
        isVehicleHistoryUiEnabled: company.isVehicleHistoryUiEnabled === true,
        showNotesOnEveryTour: company.showNotesOnEveryTour === true,
        isFilesUploadEnabled: company.isFilesUploadEnabled === true,
        isOceanAnalyticsEnabled: company.isOceanAnalyticsEnabled === true,
        isStandalonePagesEnabled: company.isStandalonePagesEnabled === true,
        isPolPodInTransportHeaderEnabled: company.isPolPodInTransportHeaderEnabled === true,
        isMaritimeIntelligenceEnabled: company.isMaritimeIntelligenceEnabled === true,
        isOceanTransportExcelUploadEnabled: company.isOceanTransportExcelUploadEnabled === true,
        isBulkTourUserAssignmentEnabled: company.isBulkTourUserAssignmentEnabled === true,
      },
    };
  }, [company]);

  const tourCounts: TourCounts = React.useMemo(
    () => ({
      road: company?.roadToursCount ?? 0,
      ocean: company?.oceanToursCount ?? 0,
      air: company?.airToursCount ?? 0,
    }),
    [company?.roadToursCount, company?.oceanToursCount, company?.airToursCount],
  );

  if (result.loading) {
    return { loading: true };
  }

  if (isNil(company) || isNil(mappedCompany) || isNil(permissions)) {
    const isUnauthenticated = ServerError.is(result.error) && result.error.statusCode === 401;
    if (!isUnauthenticated) {
      errorHandler?.captureError(
        new CustomError('Failed to fetch a company for permissions check', {
          metadata: { company_id: companyId, data: result.data },
        }),
      );
    }

    return {
      loading: false,
      error: { type: 'not-found' },
    };
  }

  if (company?.isUIDisabled) {
    errorHandler?.captureError(
      new CustomError('Trying to access company which has disabled UI', {
        metadata: { company_id: companyId },
      }),
    );

    return {
      loading: false,
      error: {
        type: 'unknown',
        message: (
          <Localized id="company.disabledUI.error.message">
            This company does not have access to the {{ PRODUCT_NAME }}
          </Localized>
        ),
      },
    };
  }

  return {
    loading: false,
    error: undefined,
    data: {
      company: mappedCompany,
      permissions,
      tourCounts,
      userInCompany: company.me ?? null,
      userOnTransporeon: company.me?.transporeonPlatform ?? null,
    },
  };
}

export const ViewerCompanyContext = React.createContext<ViewerCompany | null>(null);

export const useViewerCompany = () => {
  const viewerCompany = React.useContext(ViewerCompanyContext);

  if (viewerCompany === null) {
    throw new Error('useViewerCompany must be used within a ViewerCompanyContext');
  }

  return viewerCompany;
};

// Non-throwing variant for components that can be rendered both inside the authenticated company
// app and in the public shared-view shell (which has no company context). Returns null when there
// is no viewer company.
export const useViewerCompanyOptional = (): ViewerCompany | null => {
  return React.useContext(ViewerCompanyContext);
};

export { ANALYTICS_KEY, SHARED_VIEWS_KEY, useFeaturesVisited } from '../../lib/util/use_features_visited';
export type { FeaturesVisited } from '../../lib/util/use_features_visited';
export { useLastViewedCompanyId } from '../../lib/util/use_last_viewed_context';
